import { useMemo, useState } from 'react';

export default function RiskScoreHistory({ history = [], summary = {}, onRangeChange }) {
  const [hoveredDate, setHoveredDate] = useState(null);
  const [selectedMetric, setSelectedMetric] = useState('siteScore');

  // Get color based on risk level
  const getColor = (riskLevel) => {
    switch (riskLevel) {
      case 'CRITICAL':
        return '#ef4444'; // red-500
      case 'HIGH':
        return '#f97316'; // orange-500
      case 'MEDIUM':
        return '#eab308'; // amber-300
      case 'LOW':
        return '#10b981'; // emerald-500
      default:
        return '#6b7280'; // gray-500
    }
  };

  const metrics = [
    { key: 'siteScore', label: 'Site Score', color: '#8b5cf6' },
    { key: 'alertFreqScore', label: 'Alert Frequency', color: '#3b82f6' },
    { key: 'severityScore', label: 'Severity', color: '#ec4899' },
    { key: 'complianceScore', label: 'Compliance', color: '#f59e0b' },
    { key: 'incidentScore', label: 'Incidents', color: '#ef4444' },
  ];

  if (history.length === 0) {
    return (
      <div className="glass p-6 text-center">
        <p className="text-sm text-slate-400">No historical data available</p>
        <p className="text-xs text-slate-500 mt-1">Historical data will appear as risk scores are calculated over time</p>
      </div>
    );
  }

  const chartData = useMemo(() => {
    if (selectedMetric.startsWith('zone')) {
      // Zone history
      const zoneId = selectedMetric.replace('zone-', '');
      return history.map((entry, idx) => ({
        idx,
        date: entry.dateStr,
        value: entry.zones.find(z => z.zoneId === zoneId)?.score || null,
        fullDate: entry.date,
      }));
    } else {
      // Site score history
      return history.map((entry, idx) => ({
        idx,
        date: entry.dateStr,
        value: entry.siteScore[selectedMetric] || 0,
        riskLevel: entry.siteScore.riskLevel,
        fullDate: entry.date,
      }));
    }
  }, [history, selectedMetric]);

  const validData = chartData.filter(d => d.value !== null);

  if (validData.length === 0) {
    return (
      <div className="glass p-6 text-center">
        <p className="text-sm text-slate-400">No data for selected metric</p>
      </div>
    );
  }

  const minValue = Math.min(...validData.map(d => d.value || 0));
  const maxValue = Math.max(...validData.map(d => d.value || 0));
  const range = maxValue - minValue || 1;

  const chartWidth = 600;
  const chartHeight = 200;
  const padding = { top: 20, right: 20, bottom: 30, left: 50 };
  const graphWidth = chartWidth - padding.left - padding.right;
  const graphHeight = chartHeight - padding.top - padding.bottom;

  const xStep = validData.length > 1 ? graphWidth / (validData.length - 1) : 0;

  const getY = (value) => {
    if (range === 0) return padding.top + graphHeight / 2;
    return padding.top + graphHeight - ((value - minValue) / range) * graphHeight;
  };

  const getX = (idx) => padding.left + idx * xStep;

  // Build SVG path
  const pathPoints = validData.map((d, idx) => `${getX(idx)},${getY(d.value)}`).join(' L ');
  const path = validData.length > 0 ? `M ${pathPoints}` : '';

  const metric = metrics.find(m => m.key === selectedMetric);
  const metricColor = metric?.color || '#6b7280';

  // Data freshness badge
  const { dataFreshness, confidence, agesDays } = summary;
  const freshnessBadgeColor = (() => {
    if (dataFreshness === 'fresh') return 'bg-green-100 text-green-700 border-green-300';
    if (dataFreshness === 'recent') return 'bg-blue-100 text-blue-700 border-blue-300';
    if (dataFreshness === 'adequate') return 'bg-amber-100 text-amber-700 border-amber-300';
    return 'bg-red-100 text-red-700 border-red-300';
  })();

  return (
    <div className="space-y-4">
      <div className="glass p-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-sm font-semibold text-slate-700">Risk Score History</h3>
            <p className="text-xs text-slate-500 mt-1">Trend over last {summary.days || 30} days</p>
          </div>
          <div className={`px-2 py-1 rounded-full border text-xs font-medium ${freshnessBadgeColor}`}>
            {dataFreshness === 'fresh' && '✓ Fresh'}
            {dataFreshness === 'recent' && '↻ Recent'}
            {dataFreshness === 'adequate' && '~ Adequate'}
            {dataFreshness === 'stale' && '⚠ Stale'}
          </div>
        </div>

        {/* Metric Selector */}
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          {metrics.map(m => (
            <button
              key={m.key}
              onClick={() => setSelectedMetric(m.key)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                selectedMetric === m.key
                  ? 'bg-slate-800 text-white border border-slate-600'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200 border border-transparent'
              }`}
              style={
                selectedMetric === m.key
                  ? { borderColor: m.color, backgroundColor: m.color + '20', color: m.color }
                  : {}
              }
            >
              {m.label}
            </button>
          ))}
        </div>

        {/* Chart */}
        <div className="relative">
          <svg
            viewBox={`0 0 ${chartWidth} ${chartHeight}`}
            className="w-full"
            style={{ height: '250px' }}
          >
            {/* Grid lines */}
            {[0, 0.25, 0.5, 0.75, 1].map(pct => {
              const value = minValue + range * pct;
              const y = getY(value);
              return (
                <g key={pct}>
                  <line
                    x1={padding.left}
                    y1={y}
                    x2={chartWidth - padding.right}
                    y2={y}
                    stroke="#e2e8f0"
                    strokeWidth="1"
                  />
                  <text
                    x={padding.left - 5}
                    y={y + 4}
                    textAnchor="end"
                    className="text-[10px]"
                    fill="#94a3b8"
                  >
                    {Math.round(value)}
                  </text>
                </g>
              );
            })}

            {/* Line chart */}
            <path
              d={path}
              fill="none"
              stroke={metricColor}
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />

            {/* Data points */}
            {validData.map((d, idx) => (
              <circle
                key={idx}
                cx={getX(idx)}
                cy={getY(d.value)}
                r="4"
                fill={metricColor}
                stroke="white"
                strokeWidth="2"
                style={{ cursor: 'pointer' }}
                onMouseEnter={() => setHoveredDate(d.date)}
                onMouseLeave={() => setHoveredDate(null)}
              />
            ))}

            {/* X-axis labels */}
            {validData
              .filter((_, i) => i % Math.max(1, Math.floor(validData.length / 8)) === 0)
              .map((d, idx) => (
                <text
                  key={idx}
                  x={getX(idx)}
                  y={chartHeight - 5}
                  textAnchor="middle"
                  className="text-[9px]"
                  fill="#94a3b8"
                >
                  {d.date}
                </text>
              ))}
          </svg>

          {/* Tooltip */}
          {hoveredDate && (
            <div className="absolute top-0 left-0 bg-slate-800 text-white px-2 py-1 rounded text-xs whitespace-nowrap border border-slate-600 z-10"
              style={{ left: `${(chartData.findIndex(d => d.date === hoveredDate) / (validData.length - 1)) * 85}%` }}
            >
              <div className="font-semibold">{hoveredDate}</div>
              <div>
                {selectedMetric.startsWith('zone')
                  ? `Zone Score: ${validData.find(d => d.date === hoveredDate)?.value?.toFixed(1) || 'N/A'}`
                  : `${metric?.label || 'Score'}: ${validData.find(d => d.date === hoveredDate)?.value?.toFixed(1) || 'N/A'}`}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Data Confidence Indicator */}
      <div className="glass p-4">
        <h4 className="text-xs font-semibold text-slate-600 mb-3">Data Confidence</h4>
        <div className="space-y-2">
          {/* Confidence Score */}
          <div>
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-slate-600">Overall Confidence</span>
              <span className="font-bold text-slate-700">{Math.round(confidence)}%</span>
            </div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{
                  width: `${confidence}%`,
                  backgroundColor:
                    confidence >= 80 ? '#10b981' : confidence >= 60 ? '#f59e0b' : '#ef4444',
                }}
              />
            </div>
          </div>

          {/* Data Age */}
          <div className="text-xs text-slate-500 pt-2 border-t border-slate-200">
            <p>
              Last calculated: <strong>{Math.round(agesDays * 10) / 10}</strong> days ago
            </p>
            <p className="mt-1">
              Data freshness: <strong className="capitalize">{dataFreshness}</strong>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
